import {
  ActionMenuItem,
  Caption,
  ControlledNumberField,
  ControlledSelect,
  DisplayValue,
  FieldsetCard,
  FormContainer,
  FormSetup,
  OverlineCardHeader,
  PageContainer,
  SplitContainer,
  useTranslatedOptionEnum,
} from '@if/mui'
import { EFinancingProgram, EPaymentFrequency, EProvince, getEProvinceList } from '@if/core/types'
import { formatCurrency, formatDate, formatPercentage } from '@if/core/utils'

import { Card, CardContent, Stack } from '@mui/material'
import { useComputeProductsLoan } from '@src/data/api/calculator-api/calculator-api'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { CalculatorSchema, CalculatorSchemaType } from '@src/types/calculatorSchema'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { getFirstPaymentOnMinMax } from '@src/types/WorksheetSchema'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Module-scope so the reference is stable across renders (captures no props/state).
const useFormSetup = (): FormSetup<typeof CalculatorSchema> => ({ schema: CalculatorSchema })

const ProductsCalculator = () => {
  const { t } = useTranslation()

  const [calculatorData, setCalculatorData] = useState<ProductsComputeRequestDto | undefined>(undefined)
  const [calculatedRate] = useComputeProductsLoan(calculatorData)

  const programConfig = programConfigByFinancingProgramId(EFinancingProgram.Products)
  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const paymentFrequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', programConfig.frequencyPayments, 'enum')
  const termsOptions = useMemo(
    () =>
      programConfig.termList.map((value: number) => (
        <ActionMenuItem key={value} value={value}>{`${value} ${t('common.months')}`}</ActionMenuItem>
      )),
    [t, programConfig],
  )

  // Compute-on-submit, driven by the filters form: any field change auto-submits
  // (debounced), feeding onSubmit's request into the aside card. No persistence.
  const handleSubmit = useCallback((data: CalculatorSchemaType) => {
    const deliveryOn = formatDate(new Date())
    setCalculatorData({
      stateIso: data.stateIso ?? EProvince.Nunavut,
      amountRequested: data.amountRequested ?? 0,
      frequency: data.frequency!,
      term: data.terms!,
      deliveryOn,
      firstPaymentDate: formatDate(getFirstPaymentOnMinMax(deliveryOn, data.frequency ?? EPaymentFrequency.Monthly)[0]),
    })
  }, [])

  const showAnnualFees = (calculatedRate?.annualFees ?? 0) > 0

  return (
    <PageContainer
      title={t('calculator.productsCalculatorTitle')}
      subtitle={t('calculator.productsCalculatorSubtitle')}
    >
      <FormContainer mode="filters" useFormSetup={useFormSetup} onSubmit={handleSubmit} hideActions>
        {({ control }) => (
          <SplitContainer
            main={
              <FieldsetCard title={t('calculator.loanDetails')}>
                <Stack spacing={2}>
                  <ControlledSelect
                    name="stateIso"
                    control={control}
                    options={provinceOptions}
                    label={t('calculator.province')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledSelect
                    name="frequency"
                    control={control}
                    options={paymentFrequencyOptions}
                    label={t('calculator.frequency.label')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledSelect
                    name="terms"
                    control={control}
                    options={termsOptions}
                    label={t('calculator.terms')}
                    disableEmptyOption
                    fullWidth
                  />
                  <ControlledNumberField
                    name="amountRequested"
                    control={control}
                    label={t('calculator.amountRequested')}
                    isMoney
                    fullWidth
                  />
                </Stack>
              </FieldsetCard>
            }
            aside={
              <Card>
                <OverlineCardHeader title={t('calculator.monthlyPayment')} />
                <CardContent>
                  <Stack spacing={1}>
                    <DisplayValue>{formatCurrency(calculatedRate?.paymentAmountForFrequency ?? 0)}</DisplayValue>
                    {calculatedRate && (
                      <>
                        <Caption>
                          {t('calculator.totalInterestCosts', {
                            rate: formatPercentage(calculatedRate.interestRate),
                          })}
                        </Caption>
                        <Caption>
                          {t('calculator.effectiveRate', { effectiveRate: calculatedRate.effectiveRate })}
                        </Caption>
                        {showAnnualFees && (
                          <Caption>{t('calculator.annualFees', { annualFees: calculatedRate.annualFees })}</Caption>
                        )}
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            }
          />
        )}
      </FormContainer>
    </PageContainer>
  )
}

export default memo(ProductsCalculator)
